import { ListBase, ListProps, useDataProvider, useListContext, useNotify, usePermissions } from "react-admin";
import React, { useMemo, useState } from "react";
import { Button } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { FilterField } from "@src/components/Common/DataList";
import { NetOrcaTableListV1, TableListColumn } from "@src/components/Common/NetOrcaTableList";
import { ExtendedDataProvider } from "@src/types";
import PackDashboardTabs from "../PackDashboard/PackDashboardTabs";
import DocumentRow from "./DocumentRow";
import "./Documents.css";

const resource = "serviceowner_pack_documents";

const DocumentListView = ({ service_id }: { service_id: any }) => {
    const { refetch } = useListContext();
    const { permissions } = usePermissions();
    const dataProvider: ExtendedDataProvider = useDataProvider();
    const notify = useNotify();
    const [updatingIds, setUpdatingIds] = useState<number[]>([]);
    const isAdmin = permissions === "ADMIN";

    const handleToggleEnabled = async (record: any) => {
        setUpdatingIds(prev => [...prev, record.id]);
        try {
            await dataProvider.update(resource, {
                id: record.id,
                data: { enabled: !record.enabled },
                previousData: record,
            });
            notify(`Document ${record.enabled ? 'disabled' : 'enabled'} successfully`);
            refetch();
        } catch (error: any) {
            notify(`Failed to update document: ${error.message}`, 'error');
        } finally {
            setUpdatingIds(prev => prev.filter(id => id !== record.id));
        }
    };

    // The one thing worth narrowing by, and it has a column of its own — so it
    // sits on that header and the list needs no filter panel.
    const filterFields: FilterField[] = useMemo(() => [
        {
            name: 'service_id',
            label: 'Service',
            type: 'select',
            resource: 'services',
            dataCy: 'service-selector',
        },
    ], []);

    const columns: TableListColumn[] = useMemo(() => [
        { label: 'Filename' },
        { label: 'Size' },
        { label: 'Preview' },
        { label: 'Service', filters: ['service_id'], dataCy: 'column-service' },
        // the last track is the add button's, over each row's controls
    ], []);

    return (
        <NetOrcaTableListV1
            className="netorca-doc"
            dataCy="document-result-list"
            fields={filterFields}
            columns={columns}
            template="var(--doc-cols)"
            banner={<PackDashboardTabs />}
            emptyText="No documents found"
            noun="document"
            headerTrailing={isAdmin && (
                <Link to={`/${resource}/create?service_id=${service_id}`}>
                    <Button className="doc-create" type="primary" size="small" icon={<PlusOutlined />}>
                        Add document
                    </Button>
                </Link>
            )}
            renderRow={(record) => (
                <DocumentRow
                    key={record.id}
                    record={record}
                    resource={resource}
                    isAdmin={isAdmin}
                    updating={updatingIds.includes(record.id)}
                    onToggleEnabled={handleToggleEnabled}
                    refetch={refetch}
                />
            )}
        />
    );
};

const DocumentList = (props: ListProps) => (
    // page size is the batch the infinite scroll fetches
    <ListBase perPage={25} {...props}>
        <DocumentListView service_id={props?.filter?.service_id} />
    </ListBase>
);

export default DocumentList;
